import React, { useEffect, useState, useCallback } from "react";
import { Search, Loader2, X } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { toast } from "sonner";

const PIPELINES = {
    client: ["Received", "Reviewing", "Shortlisted", "In Conversation", "Onboarded", "Declined"],
    doctor: ["Awaiting Payment", "Under Review", "Diagnosis Scheduled", "Prescription Sent", "Closed"],
    academy: ["Application Received", "Screening", "Test", "Interview", "Scholarship Offered", "Selected", "Rejected"],
    career: ["Applied", "Under Review", "Assignment", "Interview", "Final Decision"],
};

const TITLES = {
    client: "Brands",
    doctor: "Dr. Aangan Theory",
    academy: "Aangan Academy",
    career: "Careers",
};

function nameOf(doc) {
    return doc?.data?.patient_name || doc?.data?.full_name || doc?.data?.business_name || "—";
}

export default function AdminPipeline({ pipeline }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [active, setActive] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { type: pipeline };
            if (status) params.status = status;
            if (q) params.q = q;
            const { data } = await api.get("/admin/applications", { params });
            setRows(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [pipeline, status, q]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = async (newStatus) => {
        if (!active) return;
        setBusy(true);
        try {
            await api.patch(`/admin/applications/${active.tracking_id}`, {
                status: newStatus,
                note: note || null,
            });
            toast.success("Updated");
            setNote("");
            setActive({ ...active, status: newStatus, notes: [...(active.notes || []), { text: note, by: "you", at: new Date().toISOString() }] });
            load();
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AdminShell title={`${TITLES[pipeline]} pipeline`}>
            {/* filters */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-6">
                <div className="flex items-center gap-3 border-b border-white/15 focus-within:border-saffron flex-1 lg:max-w-md">
                    <Search size={16} className="text-white/45" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search by ID, name or email..."
                        className="field-input border-none flex-1"
                        data-testid="admin-search-input"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setStatus("")}
                        className={`tag ${!status ? "is-active" : ""}`}
                        data-testid="filter-all"
                    >
                        All
                    </button>
                    {PIPELINES[pipeline].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            className={`tag ${status === s ? "is-active" : ""}`}
                            data-testid={`filter-${s.replace(/\s/g, "-")}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="card-surface overflow-hidden" data-testid="admin-pipeline-table">
                <div className="grid grid-cols-12 px-6 py-3 text-xs uppercase tracking-widest text-white/45 border-b border-white/8">
                    <div className="col-span-3">Tracking ID</div>
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3">Status</div>
                    <div className="col-span-3 text-right">Submitted</div>
                </div>
                {loading ? (
                    <div className="p-10 grid place-items-center">
                        <Loader2 className="animate-spin text-saffron" size={20} />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-white/45 text-sm">No applications yet.</div>
                ) : (
                    rows.map((r) => (
                        <button
                            key={r.tracking_id}
                            onClick={() => setActive(r)}
                            data-testid={`row-${r.tracking_id}`}
                            className="grid grid-cols-12 px-6 py-4 text-sm border-b border-white/5 hover:bg-white/[0.03] text-left w-full transition-colors"
                        >
                            <div className="col-span-3 font-mono text-saffron text-xs">{r.tracking_id}</div>
                            <div className="col-span-3 text-white truncate">{nameOf(r)}</div>
                            <div className="col-span-3">
                                <span className="tag">{r.status}</span>
                                {r.payment_status === "paid" && <span className="tag ml-2 is-active">Paid</span>}
                            </div>
                            <div className="col-span-3 text-right text-white/55 text-xs">
                                {new Date(r.created_at).toLocaleString()}
                            </div>
                        </button>
                    ))
                )}
            </div>

            {/* Side drawer */}
            {active && (
                <div className="fixed inset-0 z-50 flex" data-testid="admin-detail-drawer">
                    <div className="flex-1 bg-black/70 backdrop-blur" onClick={() => setActive(null)} />
                    <div className="w-full max-w-xl bg-[#0a0a0a] border-l border-white/10 overflow-y-auto">
                        <div className="p-6 lg:p-8">
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="eyebrow text-saffron mb-2">{TITLES[pipeline]}</div>
                                    <div className="font-mono text-xl text-white">{active.tracking_id}</div>
                                </div>
                                <button onClick={() => setActive(null)} className="text-white/55 hover:text-white" data-testid="close-drawer">
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <div className="eyebrow mb-3">Current Status</div>
                                    <div className="flex flex-wrap gap-2">
                                        {PIPELINES[pipeline].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => updateStatus(s)}
                                                disabled={busy}
                                                className={`tag ${active.status === s ? "is-active" : ""}`}
                                                data-testid={`set-status-${s.replace(/\s/g, "-")}`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div className="eyebrow mb-2">Note (optional, sent with status update)</div>
                                    <textarea
                                        rows={3}
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        className="field-input"
                                        placeholder="e.g. Welcome to the screening round!"
                                        data-testid="admin-note-input"
                                    />
                                </div>

                                <div className="hairline" />

                                <div>
                                    <div className="eyebrow mb-3">Submission</div>
                                    <dl className="space-y-2 text-sm">
                                        {Object.entries(active.data || {}).map(([k, v]) =>
                                            ["file_paths", "symptoms"].includes(k) ? null : (
                                                <div key={k} className="grid grid-cols-3 gap-3">
                                                    <dt className="text-white/45 col-span-1">{k.replace(/_/g, " ")}</dt>
                                                    <dd className="text-white col-span-2 break-words">{String(v || "—")}</dd>
                                                </div>
                                            )
                                        )}
                                        {active.data?.symptoms?.length > 0 && (
                                            <div className="grid grid-cols-3 gap-3">
                                                <dt className="text-white/45 col-span-1">symptoms</dt>
                                                <dd className="text-white col-span-2 flex flex-wrap gap-1">
                                                    {active.data.symptoms.map((s) => (
                                                        <span key={s} className="tag">{s}</span>
                                                    ))}
                                                </dd>
                                            </div>
                                        )}
                                        {active.data?.file_paths?.length > 0 && (
                                            <div className="grid grid-cols-3 gap-3">
                                                <dt className="text-white/45 col-span-1">files</dt>
                                                <dd className="text-white col-span-2 space-y-1">
                                                    {active.data.file_paths.map((id) => (
                                                        <a
                                                            key={id}
                                                            href={`${api.defaults.baseURL}/files/${id}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="block text-saffron hover:underline text-xs font-mono"
                                                        >
                                                            {id}
                                                        </a>
                                                    ))}
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>

                                {active.notes?.length > 0 && (
                                    <div>
                                        <div className="eyebrow mb-3">Notes ({active.notes.length})</div>
                                        <div className="space-y-3">
                                            {active.notes.map((n, i) => (
                                                <div key={i} className="border-l border-saffron/40 pl-3 text-sm text-white/75">
                                                    {n.text}
                                                    <div className="text-xs text-white/45 mt-1">
                                                        {n.by} · {new Date(n.at).toLocaleString()}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminShell>
    );
}
